import { EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';

// State to manage the slash command menu visibility and position
export interface SlashCommandState {
  open: boolean;
  from: number; // Start position of the slash command (the '/')
  to: number; // End position (cursor)
  filter: string; // Text typed after '/'
  top: number;
  left: number;
}

const CLOSED_STATE: SlashCommandState = {
  open: false,
  from: 0,
  to: 0,
  filter: '',
  top: 0,
  left: 0,
};

// View plugin to detect slash command triggers
export const slashCommandPlugin = (
  onStateChange: (state: SlashCommandState) => void
) =>
  ViewPlugin.fromClass(
    class {
      private pendingCheck: number | null = null;

      constructor(view: EditorView) {
        this.scheduleCheck(view);
      }

      // Schedule check after the update cycle completes (required for coordsAtPos)
      scheduleCheck(view: EditorView) {
        if (this.pendingCheck !== null) {
          cancelAnimationFrame(this.pendingCheck);
        }
        this.pendingCheck = requestAnimationFrame(() => {
          this.pendingCheck = null;
          this.checkForSlash(view);
        });
      }

      checkForSlash(view: EditorView) {
        const state = view.state;
        const { from, to } = state.selection.main;

        if (from !== to) {
          onStateChange(CLOSED_STATE);
          return;
        }

        const line = state.doc.lineAt(from);
        const lineText = line.text;
        const linePos = from - line.from;
        const textBeforeCursor = lineText.slice(0, linePos);

        // Match '/' at start of line, optionally followed by filter text
        const match = textBeforeCursor.match(/^\/(\w*)$/);

        if (match) {
          const filter = match[1] || '';
          const slashPos = line.from;
          const coords = view.coordsAtPos(slashPos);

          if (coords) {
            onStateChange({
              open: true,
              from: slashPos,
              to: from,
              filter,
              top: coords.bottom + 4,
              left: coords.left,
            });
            return;
          }
        }

        onStateChange(CLOSED_STATE);
      }

      update(update: ViewUpdate) {
        if (
          update.docChanged ||
          update.selectionSet ||
          update.viewportChanged
        ) {
          this.scheduleCheck(update.view);
        }
      }

      destroy() {
        if (this.pendingCheck !== null) {
          cancelAnimationFrame(this.pendingCheck);
        }
      }
    }
  );
